import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { taskKeys } from '@lib/keys'
import { useUserId } from './useUserId'
import {
  deselectTask,
  getSelectedTasks,
  getAssignedTasks,
  selectTask,
  getNonProjectTasks,
  getPinnedTasks,
  pinTask,
  unpinTask,
} from '@lib/api'
import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { useEntries } from '@hooks/useEntries'

interface WeekRangeOptions {
  start: DateTime
  end: DateTime
}

export const useAssignedTasksQuery = (
  { enabled, active }: { enabled: boolean; active: boolean | undefined } = {
    enabled: true,
    active: true,
  },
) => {
  const userId = useUserId()

  return useQuery(
    taskKeys.assigned(userId, active),
    () => getAssignedTasks(userId, active),
    {
      enabled,
    },
  )
}

export const useSelectedTasks = ({ start, end }: WeekRangeOptions) => {
  const userId = useUserId()

  return useQuery(taskKeys.selected(userId, start, end), () =>
    getSelectedTasks(userId, start, end),
  )
}

export const useSelectTask = ({ start, end }: WeekRangeOptions) => {
  const userId = useUserId()
  const queryClient = useQueryClient()

  return useMutation(
    (task: SelectedTask) => {
      return selectTask(userId, start, task.id)
    },
    {
      onMutate: async (task) => {
        // cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(taskKeys.selected(userId, start, end))

        // snapshot the previous value
        const previousTasks = queryClient.getQueryData<SelectedTask[]>(
          taskKeys.selected(userId, start, end),
        )

        // optimistically update to the new value
        if (previousTasks) {
          queryClient.setQueryData(taskKeys.selected(userId, start, end), [
            ...previousTasks,
            task,
          ])
        }

        // Return a context object with the snapshotted value
        return { previousTasks }
      },
      onError: (_err, _task, context) => {
        queryClient.setQueryData(
          taskKeys.selected(userId, start, end),
          context?.previousTasks,
        )
      },
      onSettled: () => {
        void queryClient.invalidateQueries(
          taskKeys.selected(userId, start, end),
        )
        void queryClient.invalidateQueries(taskKeys.nonProject)
      },
    },
  )
}

export const useDeselectTask = ({ start, end }: WeekRangeOptions) => {
  const userId = useUserId()
  const queryClient = useQueryClient()

  return useMutation(
    (task: SelectedTask) => {
      return deselectTask(userId, start, task.id)
    },
    {
      onMutate: async (task) => {
        // cancel any outgoing refetches (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries(taskKeys.selected(userId, start, end))

        // snapshot the previous value
        const previousTasks = queryClient.getQueryData<SelectedTask[]>(
          taskKeys.selected(userId, start, end),
        )

        // optimistically update to the new value
        if (previousTasks) {
          queryClient.setQueryData(
            taskKeys.selected(userId, start, end),
            previousTasks.filter((t) => t.id !== task.id),
          )
        }

        // Return a context object with the snapshotted value
        return { previousTasks }
      },
      onError: (_err, _task, context) => {
        queryClient.setQueryData(
          taskKeys.selected(userId, start, end),
          context?.previousTasks,
        )
      },
      onSettled: () => {
        void queryClient.invalidateQueries(
          taskKeys.selected(userId, start, end),
        )
        void queryClient.invalidateQueries(taskKeys.nonProject)
      },
    },
  )
}

export const useNonProjectTasks = ({ enabled } = { enabled: true }) => {
  return useQuery(taskKeys.nonProject, getNonProjectTasks, { enabled })
}

export const usePinnedTasks = ({ enabled } = { enabled: true }) => {
  const userId = useUserId()
  return useQuery(taskKeys.pinned(userId), () => getPinnedTasks(userId), {
    enabled,
  })
}

export const usePinnedTask = (taskId: number) => {
  const pinnedTasksQuery = usePinnedTasks()
  const pinnedTask = pinnedTasksQuery.data?.find((task) => task.id === taskId)
  return {
    isError: pinnedTasksQuery.isError,
    data: pinnedTask,
    isLoading: pinnedTasksQuery.isLoading,
  }
}

export const usePinTask = () => {
  const userId = useUserId()
  const queryClient = useQueryClient()

  return useMutation((taskId: number) => pinTask(userId, taskId), {
    onSuccess: () => {
      void queryClient.invalidateQueries(taskKeys.pinned(userId))
    },
  })
}

export const useUnpinTask = () => {
  const userId = useUserId()
  const queryClient = useQueryClient()

  return useMutation((taskId: number) => unpinTask(userId, taskId), {
    onSuccess: () => {
      void queryClient.invalidateQueries(taskKeys.pinned(userId))
    },
  })
}

export const useTaskIdsWithEntries = (
  userId: number,
  { start, end }: WeekRangeOptions,
) => {
  const entriesQuery = useEntries({ userId, start, end })

  const taskIdsWithEntries = useMemo(() => {
    const taskIds = new Set<number>()
    const entries = entriesQuery.data ?? []

    entries.forEach((entry) => {
      /**
       * As of this writing, entries are 'deleted' by reducing the duration to 0.
       *
       * - AF 11/28/22
       */
      if (entry.duration > 0) taskIds.add(entry.task.id)
    })

    return taskIds
  }, [entriesQuery.data])

  return taskIdsWithEntries
}
